class WebSocketService {
  constructor() {
    this.socket = null;
    this.reconnectInterval = 3000;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.listeners = {};
    this.isConnecting = false;
  }

  // Decode JWT to get expiration time
  decodeToken(token) {
    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload));
    } catch (e) {
      return null;
    }
  }

  // Check if token is expiring within given seconds (default 60s)
  isTokenExpiring(token, withinSeconds = 60) {
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return true;
    const expiresAt = decoded.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    return expiresAt - now < withinSeconds * 1000;
  }

  // Refresh the access token using refresh token
  async refreshAccessToken() {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await fetch(
        `${window.location.protocol}//${window.location.host}/api/v1/auth/refresh/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh: refreshToken }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.access);
        return data.access;
      } else {
        // Refresh token is also expired, clear tokens
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        return null;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  }

  // Get valid token, refreshing if needed
  async getValidToken() {
    let token = localStorage.getItem('token');
    if (!token) return null;

    if (this.isTokenExpiring(token)) {
      token = await this.refreshAccessToken();
    }
    return token;
  }

  connect(token) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return;
    }

    if (this.isConnecting) {
      return;
    }

    if (!token) {
      return;
    }

    this.isConnecting = true;
    this.reconnectAttempts = 0; // Reset reconnect counter on fresh connect

    // Determine WebSocket URL based on current location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // In local dev, frontend is on 3000 but WebSocket is on backend port 8000
    let host = window.location.host;
    if (host.includes('localhost:3000') || host.includes('127.0.0.1:3000')) {
      host = host.replace(':3000', ':8000');
    }
    // Pass JWT token as query parameter for authentication
    const wsUrl = `${protocol}//${host}/ws/conversations/?token=${token}`;

    // WebSocket connecting (token hidden for security)

    try {
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.emit('connected');
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Handle different message types
          if (data.type === 'conversation.message') {
            this.emit('message', data.data);
          } else if (data.type === 'conversation.update') {
            this.emit('conversationUpdate', data.data);
          } else if (data.type === 'alert.new') {
            this.emit('alertNew', data.data);
          } else if (data.type === 'alert.update') {
            this.emit('alertUpdate', data.data);
          } else if (data.type === 'alert.count') {
            this.emit('alertCount', data.data);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.socket.onerror = (error) => {
        this.isConnecting = false;
        this.emit('error', error);
      };

      this.socket.onclose = (event) => {
        this.isConnecting = false;
        this.emit('disconnected');

        // Attempt to reconnect with fresh token
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          setTimeout(async () => {
            // Get a valid (possibly refreshed) token before reconnecting
            const freshToken = await this.getValidToken();
            if (freshToken) {
              this.connect(freshToken);
            } else {
              // No valid token available, stop reconnecting
              this.emit('authenticationFailed');
            }
          }, this.reconnectInterval);
        } else {
          this.emit('maxReconnectAttemptsReached');
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      this.isConnecting = false;
      this.emit('error', error);
    }
  }

  disconnect() {
    this.isConnecting = false;
    this.reconnectAttempts = this.maxReconnectAttempts;
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  send(data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
    }
  }

  // Event listener management
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event, callback) {
    if (!this.listeners[event]) return;

    if (callback) {
      this.listeners[event] = this.listeners[event].filter(
        (listener) => listener !== callback
      );
    } else {
      delete this.listeners[event];
    }
  }

  emit(event, data) {
    if (!this.listeners[event]) return;

    this.listeners[event].forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in ${event} listener:`, error);
      }
    });
  }

  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }
}

// Export a singleton instance
const websocketService = new WebSocketService();
export default websocketService;
